import { useMemo } from 'react';

import { mapAPIAbsencesToAbsenceEntries } from '~/data/absences/data/mapping';
import {
  AbsenceEntry,
  AbsenceHistory,
  AbsenceStatus,
  AbsenceType,
} from '~/data/absences/types';
import { useGetLongAbsencesHistory } from '~/data/absences/useGetLongAbsencesHistory';
import { useGetShortAbsencesHistory } from '~/data/absences/useGetShortAbsencesHistory';
import { EnumListKey, useAllEnums } from '~/data/enums';

type PropsType = {
  employeeId: number;
  absenceType?: AbsenceType;
  absenceEntries: AbsenceEntry[];
};

type UseEmployeeAbsencesChangeHistoryOutput = {
  isLoading: boolean;
  failedToLoad: boolean;
  absencesHistory: AbsenceHistory<AbsenceEntry>[];
};

export const useEmployeeAbsencesChangeHistory = (
  props: PropsType,
): UseEmployeeAbsencesChangeHistoryOutput => {
  const { employeeId, absenceType, absenceEntries } = props;

  const { getEnums } = useAllEnums();

  const { longAbsenceIds, shortAbsenceIds } = useMemo(() => {
    const entriesByType = (type: AbsenceType): string[] => {
      if (absenceType === type || typeof absenceType === 'undefined') {
        return absenceEntries
          .filter(
            (e) =>
              e.type === type &&
              e.status !== AbsenceStatus.DRAFT &&
              typeof e.uid === 'string',
          )
          .map((e) => e.uid as string);
      }

      return [];
    };

    return {
      longAbsenceIds: entriesByType(AbsenceType.LONG),
      shortAbsenceIds: entriesByType(AbsenceType.SHORT),
    };
  }, [absenceType, absenceEntries]);

  const swrConfig = {
    revalidateOnFocus: false,
  };

  const longAbsencesSWR = useGetLongAbsencesHistory(
    {
      employeeId,
      absenceIds: longAbsenceIds,
    },
    swrConfig,
  );
  const { data: longHistories } = longAbsencesSWR;

  const shortAbsencesSWR = useGetShortAbsencesHistory(
    {
      employeeId,
      absenceIds: shortAbsenceIds,
    },
    swrConfig,
  );
  const { data: shortHistories } = shortAbsencesSWR;

  const { absencesHistory } = useMemo(() => {
    const absenceReasons = {
      short: getEnums(EnumListKey.ABSENCE_SHORT_REASON),
      long: getEnums(EnumListKey.ABSENCE_LONG_REASON),
    };

    const combinedHistories = [
      ...(longHistories ? longHistories : []),
      ...(shortHistories ? shortHistories : []),
    ];

    const absenceEntriesHistories: AbsenceEntry[] = combinedHistories
      ? mapAPIAbsencesToAbsenceEntries(
          combinedHistories,
          absenceReasons.long,
          absenceReasons.short,
        )
      : [];

    const absencesHistory = absenceEntries.map(
      (entry): AbsenceHistory<AbsenceEntry> => {
        const { uid } = entry;

        return {
          ...entry,
          history: absenceEntriesHistories.filter((e) => e.uid === uid),
        };
      },
    );

    return {
      absencesHistory,
    };
  }, [absenceEntries, longHistories, shortHistories, getEnums]);

  return {
    isLoading: longAbsencesSWR.isLoading || shortAbsencesSWR.isLoading,
    failedToLoad: longAbsencesSWR.failedToLoad || shortAbsencesSWR.failedToLoad,
    absencesHistory,
  };
};
